import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { motion } from 'framer-motion';

const bgDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://media.multihub.io/cdn-cgi/image/format=auto,w=1280/company/5695708787113984/01a14005-a60b-714b-b189-2864b518350d.webp',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Resort-style pool and lounge area with palm trees and apartment buildings in the background.',
    },
  },
};

export function CommunitiesHero() {
  const bg = useSlotGroup('communitieshero.background', bgDef);

  return (
    <section className="relative flex min-h-[min(42svh,360px)] w-full flex-col justify-end overflow-clip bg-[rgb(var(--ala-dark))] md:min-h-[50svh] lg:min-h-[684px]">
      <EditableSlotGroup groupPath="communitieshero.background" definition={bgDef}>
        <div className="absolute inset-0 z-0">
          <VibesitesImage
            url={
              bg.image ||
              ''
            }
            alt={bg.alt || ''}
            priority
            sizes="100vw"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[rgb(var(--ala-primary))] via-[rgb(var(--ala-primary)/0.55)] to-transparent lg:hidden" />
          <div
            className="absolute inset-0 hidden lg:block"
            style={{
              background:
                'linear-gradient(91.65deg, rgb(var(--ala-primary)) 0%, rgb(var(--ala-primary)/0) 100%)',
            }}
          />
        </div>
      </EditableSlotGroup>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="relative z-10 flex flex-col gap-4 px-6 pb-24 pt-6 lg:pb-[clamp(22px,6vw,87px)] lg:pl-[clamp(16px,6vw,87px)]"
      >
        <div className="flex items-center gap-3">
          <span className="h-[9px] min-h-[9px] w-[9px] rounded-full bg-[rgb(var(--ala-accent))]" aria-hidden />
          <RichTextContent
            path="communitieshero.eyebrow"
            label="Eyebrow"
            as="span"
            className="font-heading text-base font-bold uppercase leading-7 tracking-[2px] text-[rgb(var(--ala-on-primary))]"
          />
        </div>
        <RichTextContent
          path="communitieshero.title"
          label="Title"
          as="h1"
          className="font-heading text-[clamp(38px,4.86vw,70px)] font-bold leading-[1.15] text-[rgb(var(--ala-on-primary))]"
        />
      </motion.div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CommunitiesHero',
  displayName: 'Communities Hero',
  tokens: {
    'communitieshero.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>find your home</p>',
    },
    'communitieshero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Our Communities</p>',
    },
  },
  slotGroups: {
    'communitieshero.background': bgDef,
  },
};

(CommunitiesHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default CommunitiesHero;
